import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { MicIcon, CameraIcon, ClipIcon } from './icons';

// First-run guided intro. One slide per app function; each slide shows realistic
// drawn "phone" mockups (landing-page style, NOT photos) of the relevant screens,
// with a soft pulsing glow on the parts being described. In-screen text comes
// from T.onboarding.demo (translated) and all sample content is FICTIONAL —
// no real personal data. Gated in App.jsx via 'tour_pending_<id>'.

const S = '#4E7A52', SD = '#3A5C3E', BG = '#F3EEE6';
const RED = '#D04535', YEL = '#E0A83E', GRN = '#4E7A52';
const glow = { animation: 'memoPulse 1.6s ease-in-out infinite' };
const NAME = 'Anna', GROUP = 'Casa Rossi';

const NAV_ICONS = {
  today:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  search: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>,
  tasks:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z"/></svg>,
  groups: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><path d="M16 3.1a4 4 0 010 7.7"/></svg>,
  settings:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-2.8 1.1V21a2 2 0 01-4 0v-.1a1.6 1.6 0 00-2.8-1.1l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00-1.1-2.8H3a2 2 0 010-4h.1a1.6 1.6 0 001.1-2.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 002.8-1.1V3a2 2 0 014 0v.1a1.6 1.6 0 002.8 1.1l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 001.1 2.8H21a2 2 0 010 4h-.1a1.6 1.6 0 00-1.5 1z"/></svg>,
};

// ── reusable phone building blocks ──────────────────────────────────────
function Phone({ children }) {
  return (
    <div style={{ flexShrink: 0, scrollSnapAlign: 'center' }}>
      <div style={{ width: 158, height: 338, borderRadius: 22, padding: 5, background: '#222', boxShadow: '0 12px 28px rgba(0,0,0,0.24)' }}>
        <div style={{ width: '100%', height: '100%', borderRadius: 17, overflow: 'hidden', background: BG, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: 12, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 7px', flexShrink: 0 }}>
            <span style={{ fontSize: '0.36rem', fontWeight: 800, color: '#4a463f' }}>9:41</span>
            <span style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <span style={{ fontSize: '0.32rem', color: '#4a463f', letterSpacing: '0.5px' }}>●●●</span>
              <span style={{ width: 9, height: 5, border: '0.5px solid #4a463f', borderRadius: 1 }} />
            </span>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
function MemoHeader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: '#fff' }}>
      <span style={{ fontFamily: 'Georgia, serif', fontWeight: 900, fontSize: '0.78rem', color: SD, letterSpacing: '0.4px' }}>MeM<span style={{ color: S }}>◷</span></span>
      <div style={{ width: 15, height: 15, borderRadius: '50%', background: S, color: '#fff', fontSize: '0.52rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
    </div>
  );
}
function Toggle({ D, active = 0, hl = false }) {
  return (
    <div style={{ margin: 6, background: S, borderRadius: 11, display: 'flex', padding: 2, ...(hl ? glow : {}) }}>
      {D.views.map((t, i) => (
        <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.46rem', fontWeight: 700, padding: '3px 1px', borderRadius: 9, color: i === active ? SD : '#fff', background: i === active ? '#fff' : 'transparent', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
      ))}
    </div>
  );
}
function Nav({ T, active = 'today' }) {
  const items = ['today', 'search', 'tasks', 'groups', 'settings'];
  return (
    <div style={{ marginTop: 'auto', background: S, display: 'flex', padding: '4px 1px' }}>
      {items.map(k => (
        <span key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#fff', opacity: k === active ? 1 : 0.7, minWidth: 0 }}>
          <span style={{ background: k === active ? 'rgba(255,255,255,0.22)' : 'transparent', borderRadius: 6, padding: '1px 5px' }}>{NAV_ICONS[k]}</span>
          <span style={{ fontSize: '0.38rem', fontWeight: 600, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(T.nav && T.nav[k]) || k}</span>
        </span>
      ))}
    </div>
  );
}
function Fab({ children, hl, bg = S, size = 30, style }) {
  return <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 8px rgba(78,122,82,0.4)', flexShrink: 0, ...(hl ? glow : {}), ...style }}>{children}</div>;
}
function Bar({ w = '60%', h = 5, mb = 0, bg = '#e2ddd3' }) {
  return <div style={{ height: h, width: w, background: bg, borderRadius: 3, marginBottom: mb }} />;
}
function Chip({ children, fill, color }) {
  return <span style={{ fontSize: '0.44rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', background: fill ? S : '#fff', color: color || (fill ? '#fff' : '#4a463f'), border: fill ? 'none' : '1px solid #E0D9CE' }}>{children}</span>;
}
function EvCard({ c = GRN, time, title, allDay, allDayLabel, wrap = false }) {
  return (
    <div style={{ background: '#fff', borderInlineStart: `3px solid ${c}`, borderRadius: 5, margin: '0 8px 5px', padding: '5px 6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.42rem', color: '#8A837A', fontWeight: 700, marginBottom: 2 }}>{allDay ? '◍ ' + allDayLabel : time}</div>
        <div style={{ fontSize: '0.48rem', color: '#2C2A27', fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: wrap ? 'normal' : 'nowrap', display: wrap ? '-webkit-box' : 'block', WebkitLineClamp: wrap ? 2 : 1, WebkitBoxOrient: 'vertical' }}>{title}</div>
      </div>
      <div style={{ display: 'flex', gap: 3, fontSize: '0.5rem', opacity: 0.4, flexShrink: 0 }}>✉ 🗑</div>
    </div>
  );
}
function DayHead({ wd, d, temp }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '5px 9px 3px' }}>
      <span><span style={{ fontSize: '0.4rem', color: '#8A837A', fontWeight: 700 }}>{wd} </span><span style={{ fontSize: '0.62rem', fontWeight: 800, color: SD }}>{d}</span></span>
      <span style={{ fontSize: '0.42rem', color: '#8A837A' }}>{temp}</span>
    </div>
  );
}

// ── the screens (fictional, translated data) ────────────────────────────
function ScreenDay({ T, D }) {
  return (<Phone>
    <MemoHeader /><Toggle D={D} active={0} hl />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 8px 4px' }}>
      <span style={{ fontSize: '0.5rem', fontWeight: 700, color: SD }}>‹ 18 · sab ›</span>
      <Fab hl size={17}><span style={{ fontSize: '0.58rem' }}>🔊</span></Fab>
    </div>
    <div style={{ display: 'flex', gap: 3, padding: '0 8px 4px' }}><Chip fill>{NAME}</Chip><Chip>{GROUP}</Chip></div>
    <EvCard c={GRN} allDay allDayLabel={D.allDay} title={D.ev[5]} />
    <EvCard c={RED} time="15:50" title={D.ev[0]} wrap />
    <EvCard c={YEL} time="18:00" title={D.ev[1]} />
    <Fab size={26} style={{ position: 'absolute', right: 8, bottom: 40 }}><span style={{ fontSize: '1rem', lineHeight: 1 }}>＋</span></Fab>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenWeekAgenda({ T, D }) {
  return (<Phone>
    <MemoHeader /><Toggle D={D} active={1} hl />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 8px 3px' }}>
      <span style={{ fontSize: '0.47rem', fontWeight: 700, color: SD }}>‹ 13 – 19 ›</span>
      <Fab hl size={17}><span style={{ fontSize: '0.58rem' }}>🔊</span></Fab>
    </div>
    <div style={{ flex: 1, overflow: 'hidden' }}>
      <DayHead wd="LUN" d="13" temp="☀️ 34°" />
      <EvCard c={GRN} allDay allDayLabel={D.allDay} title={D.ev[5]} />
      <EvCard c={GRN} time="07:30" title={D.ev[2]} />
      <DayHead wd="MAR" d="14" temp="☁️ 39°" />
      <EvCard c={GRN} allDay allDayLabel={D.allDay} title={D.ev[3]} />
      <EvCard c={YEL} time="10:00" title={D.ev[1]} />
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function MiniMonth({ mesi, i }) {
  return (
    <div style={{ padding: 1, overflow: 'hidden' }}>
      <div style={{ fontSize: '0.34rem', color: SD, textAlign: 'center', fontWeight: 700 }}>{mesi[i]}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 0 }}>
        {Array.from({ length: 28 }).map((_, j) => <span key={j} style={{ fontSize: '0.29rem', lineHeight: 1.15, textAlign: 'center', color: '#8a857c' }}>{j + 1}</span>)}
      </div>
    </div>
  );
}
function ScreenMonth({ T, D }) {
  const wk = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
  return (<Phone>
    <MemoHeader /><Toggle D={D} active={2} />
    <div style={{ background: S, color: '#fff', margin: '0 6px', borderRadius: 8, textAlign: 'center', fontSize: '0.5rem', fontWeight: 700, padding: '2px 0' }}>‹ 07 · 2026 ›</div>
    <div style={{ display: 'flex', padding: '3px 8px 0' }}>{wk.map((d, i) => <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.4rem', color: '#8A837A' }}>{d}</span>)}</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, padding: '2px 8px' }}>
      {Array.from({ length: 35 }).map((_, i) => {
        const d = i - 1; const today = d === 18;
        const dot = [3, 8, 11, 15, 18, 22, 27].includes(d);
        return <div key={i} style={{ aspectRatio: '1', borderRadius: '50%', border: '1px solid #e6dfd4', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '0.34rem', color: d > 0 && d < 32 ? '#4a463f' : '#cfc8bd', background: today ? S : 'transparent', fontWeight: today ? 700 : 400 }}>
          <span style={{ color: today ? '#fff' : 'inherit' }}>{d > 0 && d < 32 ? d : ''}</span>
          {dot && !today && <span style={{ width: 3, height: 3, borderRadius: '50%', background: [RED, YEL, GRN][d % 3] }} />}
        </div>;
      })}
    </div>
    <div style={{ flex: 1, padding: '3px 9px', borderTop: '1px solid #ece5da' }}>
      <div style={{ fontSize: '0.42rem', fontWeight: 700, color: SD, marginBottom: 3 }}>18 · sab</div>
      {[[GRN, '15:50', 0], [YEL, '18:00', 4]].map(([c, tm, e], k) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <span style={{ width: 3, height: 11, background: c, borderRadius: 2 }} />
          <span style={{ fontSize: '0.4rem', color: '#8A837A', fontWeight: 700 }}>{tm}</span>
          <span style={{ fontSize: '0.44rem', color: '#2C2A27', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{D.ev[e]}</span>
        </div>
      ))}
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenYear({ T, D }) {
  const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  return (<Phone>
    <MemoHeader /><Toggle D={D} active={3} />
    <div style={{ textAlign: 'center', fontSize: '0.55rem', fontWeight: 800, color: SD, padding: '2px 0' }}>‹ 2026 ›</div>
    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gridTemplateRows: 'repeat(4,1fr)', gap: 2, padding: '0 4px 4px', overflow: 'hidden' }}>
      {mesi.map((_, i) => <MiniMonth key={i} mesi={mesi} i={i} />)}
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenAddTop({ T, D, hl }) {
  return (<Phone>
    <MemoHeader />
    <div style={{ padding: '8px 9px', flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, border: `1.5px solid ${S}`, borderRadius: 6, padding: '5px 6px', ...(hl ? glow : {}) }}>
        <span style={{ fontSize: '0.5rem', color: '#4a463f', fontWeight: 600 }}>{D.ev[3]}</span><span style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>⌨️</span>
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <span style={{ fontSize: '0.46rem', color: '#8A837A', fontWeight: 700 }}>18/07</span>
        <span style={{ fontSize: '0.45rem', border: '1px solid #E0D9CE', borderRadius: 4, padding: '1px 5px' }}>09 ▾</span>
        <span style={{ fontSize: '0.45rem', border: '1px solid #E0D9CE', borderRadius: 4, padding: '1px 5px' }}>30 ▾</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Switch on={false} /><span style={{ fontSize: '0.44rem', color: '#4a463f' }}>{D.allDay}</span></div>
      <div style={{ border: '1px solid #E0D9CE', borderRadius: 6, padding: '4px 6px', fontSize: '0.44rem', color: '#b4afa2', display: 'flex', alignItems: 'center', gap: 4 }}>📍 Via Roma 10</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}><Chip fill>{D.prio[0]}</Chip><Chip>{D.prio[1]}</Chip><Chip>{D.prio[2]}</Chip></div>
      <div style={{ display: 'flex', gap: 6, fontSize: '0.42rem', fontWeight: 700 }}>
        <span style={{ color: '#8E44AD' }}>{D.labels[0]}</span><span style={{ color: '#00ACC1' }}>{D.labels[1]}</span><span style={{ color: '#D04535' }}>{D.labels[2]}</span>
      </div>
      <div style={{ display: 'flex', gap: 4 }}><Chip fill>{NAME}</Chip><Chip>{D.shared}</Chip></div>
    </div>
    <div style={{ position: 'absolute', insetInlineEnd: 7, top: 46, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <Fab hl={hl} size={24}><ClipIcon /></Fab><Fab hl={hl} size={24}><CameraIcon /></Fab><Fab hl={hl} size={24}><MicIcon /></Fab>
      <Fab size={24} style={{ fontSize: '0.5rem', fontWeight: 700 }}>OK</Fab>
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenAddBottom({ T, D }) {
  return (<Phone>
    <MemoHeader />
    <div style={{ padding: '8px 9px', flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}><Chip>{D.repeat[0]}</Chip><Chip>{D.repeat[1]}</Chip></div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}><Chip fill>{D.reminder} ✕</Chip><Chip>+ 🔔</Chip></div>
      <div style={{ border: '1px solid #E0D9CE', borderRadius: 6, padding: 6, minHeight: 26, fontSize: '0.45rem', color: '#b4afa2' }}>{D.notePh}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, border: '1px solid #E0D9CE', borderRadius: 6, padding: '4px 6px' }}><span style={{ fontSize: '0.55rem' }}>📄</span><span style={{ fontSize: '0.42rem', color: '#4a463f' }}>documento.pdf</span><span style={{ marginLeft: 'auto', fontSize: '0.5rem', color: '#a8a297' }}>✕</span></div>
    </div>
    <div style={{ position: 'absolute', insetInlineEnd: 7, top: 60, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <Fab size={24}><ClipIcon /></Fab><Fab size={24}><CameraIcon /></Fab><Fab size={24}><MicIcon /></Fab>
      <Fab size={24} style={{ fontSize: '0.5rem', fontWeight: 700 }}>OK</Fab>
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenPlannerDay({ T, D }) {
  return (<Phone>
    <MemoHeader /><Toggle D={D} active={0} />
    <div style={{ textAlign: 'center', fontSize: '0.47rem', fontWeight: 700, color: SD, padding: '2px 0' }}>‹ 18 · sab ›</div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 8px', ...glow }}>
      {[0, 1, 2, 3, 4, 5, 6].map(i => {
        const occ = [1, 4, 5].includes(i);
        const title = { 1: D.ev[0], 4: D.ev[1], 5: D.ev[4] }[i];
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.4rem', color: '#a8a297', width: 16 }}>{`0${8 + i}:00`.slice(-5)}</span>
            <div style={{ flex: 1, height: 13, borderRadius: 3, background: occ ? GRN : '#eae4d9', display: 'flex', alignItems: 'center', paddingInlineStart: 5, overflow: 'hidden' }}>
              {occ && <span style={{ fontSize: '0.36rem', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>}
            </div>
          </div>
        );
      })}
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenPlannerWeek({ T, D }) {
  const cols = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
  return (<Phone>
    <MemoHeader /><Toggle D={D} active={1} />
    <div style={{ textAlign: 'center', fontSize: '0.47rem', fontWeight: 700, color: SD }}>‹ 13 – 19 ›</div>
    <div style={{ display: 'flex', padding: '3px 4px 1px' }}>{cols.map((c, i) => <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.4rem', color: '#8A837A', fontWeight: 700 }}>{c}</span>)}</div>
    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gridAutoRows: '11px', gap: 1, padding: '0 4px', ...glow }}>
      {Array.from({ length: 28 }).map((_, i) => {
        const idx = [0, 3, 7, 9, 14, 15, 16, 22].indexOf(i);
        const ccol = [RED, GRN, YEL, GRN, GRN, GRN, GRN, RED];
        return <div key={i} style={{ background: idx >= 0 ? ccol[idx] : '#eae4d9', borderRadius: 2, opacity: idx >= 0 ? 0.9 : 1 }} />;
      })}
    </div>
    <Nav T={T} active="today" />
  </Phone>);
}
function ScreenSearch({ T, D, hl }) {
  return (<Phone>
    <MemoHeader />
    <div style={{ flex: 1, padding: '6px 8px', overflow: 'hidden' }}>
      <div style={{ fontSize: '0.4rem', color: '#8A837A', fontWeight: 700, marginBottom: 4 }}>2 · «{D.ev[3]}»</div>
      <EvCard c={RED} time="10:00 · 20 lug" title={D.ev[3]} />
      <EvCard c={GRN} time="09:30 · 27 lug" title={D.ev[3]} />
    </div>
    <div style={{ margin: '0 8px 6px', display: 'flex', gap: 5, alignItems: 'center' }}>
      <div style={{ flex: 1, border: `1.5px solid ${S}`, borderRadius: 16, padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 4, ...(hl ? glow : {}) }}>
        <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>🔍</span><span style={{ fontSize: '0.44rem', color: '#2C2A27' }}>{D.ev[3]}</span>
      </div>
      <Fab hl={hl} size={24}><MicIcon /></Fab>
    </div>
    <Nav T={T} active="search" />
  </Phone>);
}
function ScreenNote({ T, D, hl }) {
  return (<Phone>
    <MemoHeader />
    <div style={{ padding: '2px 8px', flex: 1, overflow: 'hidden' }}>
      {[2, 3, 5, 0, 1].map((e, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 5, borderBottom: '1px solid #e6dfd4', padding: '6px 0' }}>
          <span style={{ fontSize: '0.47rem', color: '#2C2A27' }}>{D.ev[e]}</span>
          <span style={{ fontSize: '0.5rem', color: '#a8a297', marginLeft: 'auto' }}>✕</span>
        </div>
      ))}
    </div>
    <div style={{ margin: '0 8px 6px', display: 'flex', gap: 5, alignItems: 'flex-end' }}>
      <div style={{ flex: 1, border: `1.5px solid ${S}`, borderRadius: 8, padding: '6px 8px', fontSize: '0.44rem', color: '#b4afa2', ...(hl ? glow : {}) }}>{D.notePh}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Fab hl={hl} size={24}><span style={{ fontSize: '0.9rem', lineHeight: 1 }}>＋</span></Fab>
        <Fab hl={hl} size={24}><MicIcon /></Fab>
      </div>
    </div>
    <Nav T={T} active="tasks" />
  </Phone>);
}
function ScreenShared({ T, D, hl }) {
  return (<Phone>
    <MemoHeader />
    <div style={{ padding: '8px', flex: 1 }}>
      <div style={{ fontSize: '0.5rem', fontWeight: 800, color: SD, marginBottom: 8 }}>{D.sharedTitle}</div>
      <div style={{ display: 'flex', gap: 5, marginBottom: 10, ...(hl ? glow : {}) }}><Chip fill>{D.create}</Chip><Chip>{D.join}</Chip></div>
      <div style={{ border: '1px solid #e6dfd4', borderRadius: 8, padding: 7, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: S }} /><span style={{ fontSize: '0.46rem', fontWeight: 700, color: '#2C2A27' }}>{GROUP}</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}><Chip>A1B2C3</Chip><Chip fill>ADMIN</Chip></div>
      </div>
    </div>
    <Nav T={T} active="groups" />
  </Phone>);
}
function SummaryCard({ dark, D }) {
  const fg = dark ? '#e8eef0' : '#2C2A27';
  const cardBg = dark ? '#17212b' : '#fff';
  const rows = [[GRN, '08:00', 1], [GRN, '17:00', 0], [YEL, '19:30', 4], [RED, '20:00', 3]];
  return (<Phone>
    <div style={{ flex: 1, background: dark ? '#0e1621' : '#eef1f4', display: 'flex', flexDirection: 'column' }}>
      {/* channel header — clearly Telegram (blue) vs Email (envelope + subject) */}
      {dark
        ? <div style={{ background: '#2AABEE', color: '#fff', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.5rem', fontWeight: 800 }}><span>✈️</span>Telegram</div>
        : <div style={{ background: '#fff', borderBottom: '1px solid #e6dfd4', padding: '6px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.5rem', fontWeight: 800, color: '#4a463f' }}><span>✉️</span>Email</div>
            <div style={{ fontSize: '0.38rem', color: '#8A837A', marginTop: 2 }}>MeMo · {D.greeting} ☀️</div>
          </div>}
      <div style={{ flex: 1, padding: 8, display: 'flex', alignItems: dark ? 'flex-start' : 'center' }}>
        <div style={{ background: cardBg, borderRadius: dark ? '10px 10px 10px 2px' : 10, padding: 10, width: '100%', boxShadow: dark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '0.5rem', fontWeight: 800, color: fg, marginBottom: 1 }}>☀️ {D.greeting}, {NAME}!</div>
          <div style={{ fontSize: '0.4rem', color: dark ? '#9fb0b8' : '#8A837A', marginBottom: 8 }}>17/07/2026</div>
          {rows.map(([c, time, evi], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0 }} />
              <span style={{ fontSize: '0.42rem', color: dark ? '#c4ccd0' : '#6b665e', fontWeight: 700 }}>{time}</span>
              <span style={{ fontSize: '0.44rem', color: fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{D.ev[evi]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Phone>);
}
// Row-based Settings labels (mirror of the app), reused from the landing SET3.
const ST_LABELS = {
  it: { lang:'Lingua', tz:'Fuso orario', theme:'Tema App', notif:'Notifiche', labels:'Etichette', summary:'Riepilogo', apple:'Collega Apple', a11y:'Accessibilità', usage:'Utilizzo dati', save:'Salva impostazioni' },
  en: { lang:'Language', tz:'Time zone', theme:'App theme', notif:'Notifications', labels:'Labels', summary:'Summary', apple:'Connect Apple', a11y:'Accessibility', usage:'Data usage', save:'Save settings' },
  es: { lang:'Idioma', tz:'Zona horaria', theme:'Tema', notif:'Notificaciones', labels:'Etiquetas', summary:'Resumen', apple:'Conectar Apple', a11y:'Accesibilidad', usage:'Uso de datos', save:'Guardar' },
  de: { lang:'Sprache', tz:'Zeitzone', theme:'App-Design', notif:'Mitteilungen', labels:'Labels', summary:'Übersicht', apple:'Apple verbinden', a11y:'Barrierefreiheit', usage:'Datennutzung', save:'Speichern' },
  fr: { lang:'Langue', tz:'Fuseau', theme:'Thème', notif:'Notifications', labels:'Étiquettes', summary:'Résumé', apple:'Connecter Apple', a11y:'Accessibilité', usage:'Données', save:'Enregistrer' },
  nl: { lang:'Taal', tz:'Tijdzone', theme:'Thema', notif:'Meldingen', labels:'Labels', summary:'Overzicht', apple:'Apple koppelen', a11y:'Toegankelijkheid', usage:'Datagebruik', save:'Opslaan' },
  pt: { lang:'Idioma', tz:'Fuso horário', theme:'Tema', notif:'Notificações', labels:'Etiquetas', summary:'Resumo', apple:'Ligar Apple', a11y:'Acessibilidade', usage:'Uso de dados', save:'Guardar' },
  ru: { lang:'Язык', tz:'Часовой пояс', theme:'Тема', notif:'Уведомления', labels:'Метки', summary:'Сводка', apple:'Подключить Apple', a11y:'Доступность', usage:'Данные', save:'Сохранить' },
  pl: { lang:'Język', tz:'Strefa', theme:'Motyw', notif:'Powiadomienia', labels:'Etykiety', summary:'Podsumowanie', apple:'Połącz Apple', a11y:'Dostępność', usage:'Dane', save:'Zapisz' },
  ro: { lang:'Limbă', tz:'Fus orar', theme:'Temă', notif:'Notificări', labels:'Etichete', summary:'Rezumat', apple:'Conectează Apple', a11y:'Accesibilitate', usage:'Date', save:'Salvează' },
  zh: { lang:'语言', tz:'时区', theme:'主题', notif:'通知', labels:'标签', summary:'摘要', apple:'连接 Apple', a11y:'无障碍', usage:'数据用量', save:'保存设置' },
  ja: { lang:'言語', tz:'タイムゾーン', theme:'テーマ', notif:'通知', labels:'ラベル', summary:'要約', apple:'Apple連携', a11y:'アクセシビリティ', usage:'データ使用量', save:'保存' },
  hi: { lang:'भाषा', tz:'समय क्षेत्र', theme:'थीम', notif:'सूचनाएँ', labels:'लेबल', summary:'सारांश', apple:'Apple जोड़ें', a11y:'सुलभता', usage:'डेटा उपयोग', save:'सहेजें' },
  ko: { lang:'언어', tz:'시간대', theme:'테마', notif:'알림', labels:'라벨', summary:'요약', apple:'Apple 연결', a11y:'접근성', usage:'데이터', save:'설정 저장' },
  ar: { lang:'اللغة', tz:'المنطقة الزمنية', theme:'المظهر', notif:'الإشعارات', labels:'التصنيفات', summary:'الملخص', apple:'ربط Apple', a11y:'الوصول', usage:'البيانات', save:'حفظ' },
};
function SetBtn({ icon, children }) {
  return (
    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, background: '#fff', border: '1px solid #E0D9CE', borderRadius: 7, padding: '4px 1px', fontSize: '0.4rem', fontWeight: 700, color: '#2C2A27' }}>
      <span style={{ fontSize: '0.62rem', lineHeight: 1 }}>{icon}</span>
      <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children} ▾</span>
    </span>
  );
}
function ScreenSetMain({ T, D, lang }) {
  const L = ST_LABELS[(lang || 'it').split('-')[0]] || ST_LABELS.en;
  return (<Phone>
    <MemoHeader />
    <div style={{ padding: 8, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 16, height: 16, borderRadius: '50%', background: S, color: '#fff', fontSize: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>A</span>
        <div style={{ flex: 1 }}><span style={{ fontSize: '0.46rem', fontWeight: 700, color: '#2C2A27' }}>{NAME}</span><Bar w="60%" h={4} mb={0} /></div>
        <Chip>{D.signout}</Chip>
      </div>
      <div style={{ display: 'flex', gap: 4 }}><SetBtn icon="🌐">{L.lang}</SetBtn><SetBtn icon="🕐">{L.tz}</SetBtn><SetBtn icon="🎨">{L.theme}</SetBtn></div>
      <div style={{ display: 'flex', gap: 4 }}><SetBtn icon="🔔">{L.notif}</SetBtn><SetBtn icon="🏷️">{L.labels}</SetBtn><SetBtn icon="☀️">{L.summary}</SetBtn></div>
      <div style={{ display: 'flex', gap: 4 }}><SetBtn icon="">{L.apple}</SetBtn><SetBtn icon="👁️">{L.a11y}</SetBtn><SetBtn icon="📊">{L.usage}</SetBtn></div>
      <div style={{ background: S, color: '#fff', borderRadius: 7, padding: '6px 0', textAlign: 'center', fontSize: '0.46rem', fontWeight: 800, marginTop: 1 }}>{L.save}</div>
    </div>
    <Nav T={T} active="settings" />
  </Phone>);
}
function Switch({ on = true }) {
  return <span style={{ width: 16, height: 10, borderRadius: 6, background: on ? S : '#cfc8bd', position: 'relative', display: 'inline-block', flexShrink: 0 }}><span style={{ position: 'absolute', insetInlineEnd: on ? 1 : 'auto', insetInlineStart: on ? 'auto' : 1, top: 1, width: 8, height: 8, borderRadius: '50%', background: '#fff' }} /></span>;
}
function ScreenSetNotif({ T, D, hl }) {
  return (<Phone>
    <MemoHeader />
    <div style={{ padding: 8, flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: '0.42rem', color: '#8A837A', fontWeight: 800 }}>{D.notif}</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <span style={{ flex: 1, border: '1px solid #e6dfd4', borderRadius: 5, padding: '3px 5px', fontSize: '0.42rem', color: '#4a463f' }}>🔔 30 min</span>
        <span style={{ flex: 1, border: '1px solid #e6dfd4', borderRadius: 5, padding: '3px 5px', fontSize: '0.42rem', color: '#4a463f' }}>🔊 60%</span>
      </div>
      <div style={{ display: 'flex', gap: 5 }}>
        <span style={{ flex: 1, border: '1px solid #e6dfd4', borderRadius: 5, padding: '3px 5px', fontSize: '0.42rem', color: '#4a463f' }}>🎵 Campana</span>
        <span style={{ flex: 1, border: '1px solid #e6dfd4', borderRadius: 5, padding: '3px 5px', fontSize: '0.42rem', color: '#4a463f' }}>↻ 5×</span>
      </div>
      <div style={{ fontSize: '0.42rem', color: '#8A837A', fontWeight: 800, marginTop: 2 }}>{D.summaryLbl}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, ...(hl ? glow : {}), borderRadius: 6, padding: 2 }}>
        <span style={{ fontSize: '0.44rem', color: '#4a463f' }}>{D.time}</span>
        <span style={{ border: '1px solid #e6dfd4', borderRadius: 5, padding: '2px 6px', fontSize: '0.45rem', fontWeight: 700, background: '#fff' }}>07 : 00</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Switch on /><span style={{ fontSize: '0.42rem', color: '#4a463f' }}>✈️ {D.telegram}</span></div>
      <div style={{ border: '1px solid #e6dfd4', borderRadius: 6, padding: '3px 6px', fontSize: '0.42rem', color: '#8A837A', background: '#fff', letterSpacing: '1px' }}>••• ••• •••</div>
    </div>
    <Nav T={T} active="settings" />
  </Phone>);
}

function phonesFor(key, T, D, lang) {
  switch (key) {
    case 'calendar': return [<ScreenDay key="d" T={T} D={D} />, <ScreenWeekAgenda key="w" T={T} D={D} />, <ScreenMonth key="m" T={T} D={D} />, <ScreenYear key="y" T={T} D={D} />];
    case 'add':      return [<ScreenAddTop key="a" T={T} D={D} hl />, <ScreenAddBottom key="b" T={T} D={D} />];
    case 'planner':  return [<ScreenPlannerDay key="p" T={T} D={D} />, <ScreenPlannerWeek key="pw" T={T} D={D} />];
    case 'multi':    return [<ScreenSearch key="s" T={T} D={D} hl />, <ScreenNote key="n" T={T} D={D} hl />, <ScreenShared key="sh" T={T} D={D} hl />];
    case 'summary':  return [<SummaryCard key="tg" dark D={D} />, <SummaryCard key="em" D={D} />];
    case 'settings': return [<ScreenSetMain key="s1" T={T} D={D} lang={lang} />, <ScreenSetNotif key="s2" T={T} D={D} hl />];
    default:         return [<ScreenDay key="d" T={T} D={D} />];
  }
}

export default function OnboardingSlides() {
  const { user } = useAuth();
  const lang = user?.language || 'it';
  const T = useT(lang);
  const ob = T.onboarding;
  const slides = ob?.slides || [];
  const [i, setI] = useState(0);

  function finish() {
    try { if (user?.id) localStorage.removeItem('tour_pending_' + user.id); } catch (_) {}
    window.location.href = '/';
  }
  // Missing slides/demo for this language → skip the tour. In an effect, not
  // during render (side effects mid-render violate the rules of hooks).
  const noContent = !slides.length || !ob?.demo;
  useEffect(() => { if (noContent) finish(); });
  if (noContent) return null;

  const last = i === slides.length - 1;
  const slide = slides[i];
  const phones = phonesFor(slide.key, T, ob.demo, lang);
  function next() { last ? finish() : setI(i + 1); }

  return (
    <div className="login-page" style={{ justifyContent: 'flex-start', paddingTop: 12, paddingBottom: 16, minHeight: '100dvh', overflowY: 'auto' }}>
      <style>{'@keyframes memoPulse{0%,100%{box-shadow:0 0 0 0 rgba(78,122,82,.55)}50%{box-shadow:0 0 0 8px rgba(78,122,82,0)}}'}</style>

      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={finish} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.95rem', padding: '4px', fontFamily: 'inherit' }}>{ob.skip}</button>
      </div>

      <h1 className="login-title" style={{ margin: '4px 0', textAlign: 'center' }}>{slide.title}</h1>
      <p className="login-subtitle" style={{ fontSize: '0.95rem', lineHeight: 1.4, margin: '0 0 12px', textAlign: 'center', maxWidth: 380 }}>{slide.text}</p>

      <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'flex', gap: 16, width: 'fit-content', minWidth: '100%', justifyContent: 'center', margin: '0 auto', padding: '4px 16px 10px', boxSizing: 'border-box', scrollSnapType: 'x mandatory' }}>
          {phones}
        </div>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {slides.map((_, idx) => (
            <span key={idx} style={{ width: idx === i ? 22 : 8, height: 8, borderRadius: 4, background: idx === i ? S : 'var(--color-border)', transition: 'width 0.2s, background 0.2s' }} />
          ))}
        </div>
        <button type="button" onClick={next} style={{ width: '100%', padding: '15px 0', border: 'none', borderRadius: 'var(--radius-md, 12px)', background: S, color: '#fff', fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 3px 12px rgba(78,122,82,0.35)' }}>{last ? ob.start : ob.next}</button>
      </div>
    </div>
  );
}
